import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, IconButton, Paper, SelectChangeEvent, Stack } from '@mui/material';
import { Add as CreateIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { CreateRoleModal, DeleteConfirmModal } from './components';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import { useUpdateCompanyTypeMutation } from '../../../api';
import { useAppActions } from '../../../utils/hooks';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { permissionsCheck } from '../../../utils/permission';
import {
  CreateRoleDTO,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetRolesQuery,
} from '../../../api/controllers/admin-roles';
import { Role } from '../../../types/models/roles.model';
import { useNavigate } from 'react-router-dom';
import { RouterPaths } from '../../../router/paths';

export const Roles: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const hasPermissionToEditRole = permissionsCheck(['WRITE_ROLES']);

  // api
  const { data: roles, isLoading: isCompanyTypesLoading, refetch } = useGetRolesQuery();
  const [createRole, { isLoading: isCreateCompanyTypeLoading }] = useCreateRoleMutation();
  const [deleteRole, { isLoading: isDeleteLoading }] = useDeleteRoleMutation();

  // variables
  const isLoading = isCompanyTypesLoading || isCreateCompanyTypeLoading || isDeleteLoading;

  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [name, setName] = useState<string>('');
  const [editFields, setEditFields] = useState<CreateRoleDTO | null>(null);

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditRole &&
      setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // create modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // delete modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (companyType: Role) => {
    setDeleteModalShown(true);
    setActiveRowId(companyType.id);
    setName(companyType.name);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
    setEditFields(null);
    setName('');
  };
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // create company type handler
  const onCreateRole = async () => {
    if (!editFields) return;
    const args = {
      name: editFields.name,
      comment: editFields.comment,
      authorities: editFields.authorities,
    };

    try {
      await createRole(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete company type handler
  const deleteCompanyType = async () => {
    if (!activeRowId) return;

    const args = { roleId: activeRowId };

    try {
      await deleteRole(args).unwrap();
      closeDeleteModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('users-and-roles.roles.head-cells.id') },
    { id: '2', label: t('users-and-roles.roles.head-cells.name') },
    { id: '3', label: t('users-and-roles.roles.head-cells.comment') },
    {
      id: '4',
      label: t('users-and-roles.roles.head-cells.actions'),
      hidden: !hasPermissionToEditRole,
    },
  ];

  const rows: Row[] | undefined = roles?.roles.map((it) => {
    const { id, name, comment } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'name',
        value: name,
      },
      {
        name: 'comment',
        value: comment,
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditRole,
        component: (
          <IconButton
            aria-label="delete"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openDeleteModal(it);
            }}
            disabled={isLoading}
          >
            <DeleteIcon />
          </IconButton>
        ),
      },
    ];

    return {
      cells,
      onClick: () => hasPermissionToEditRole && navigate(`${RouterPaths.Roles}/${id}`),
      hover: hasPermissionToEditRole,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateRoleModal
        open={createModalShown}
        onClose={closeCreateModal}
        onSuccess={onCreateRole}
        onCancel={closeCreateModal}
        isLoading={isLoading}
        fields={editFields}
        handleEditFields={handleEditFields}
      />

      <DeleteConfirmModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onSuccess={deleteCompanyType}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
        name={name}
      />
    </>
  );
};
